import { Fragment, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../card';
import { ColorEnum, FontWeightEnum } from '../../enums';
import styles from './IncomeSourceCard.module.css';
import Text from '../../atoms/typography/Text';
import ButtonNew from '../../molecules/button/Button';
import PencilLineIcon from '../../atoms/icons/PencilLineIcon';
import DeleteBinIcon from '../../atoms/icons/DeleteBinIcon';
import { classNames, formatPrice } from '../../utils';
import IncomeVerificationIncomeSourceTypeEnum from '../../enums/screening/IncomeVerificationIncomeSourceTypeEnum';
import { IncomeSource, IncomeSourceEmployment } from '../../../api/screening/income-verification/findIncomeSources';
import BulletSeparatedList from '../../molecules/bullet-separated-list/BulletSeparatedList';
import HighlightBlock from '../../molecules/highlightBlock/HighlightBlock';

interface IncomeSourceCardProps {
    readonly className?: string;
    readonly incomeType: IncomeVerificationIncomeSourceTypeEnum;
    readonly grossIncome?: number;
    readonly isDeleting?: boolean;
    readonly incomeSource?: IncomeSource;
    readonly onEditClick: (e: MouseEvent<HTMLButtonElement>) => void;
    readonly onDeleteClick: (e: MouseEvent<HTMLButtonElement>) => void;
    readonly disabled?: boolean;
}

const IncomeSourceCard = ({
    className,
    incomeType,
    grossIncome,
    isDeleting = false,
    incomeSource,
    onEditClick,
    onDeleteClick,
    disabled = false,
}: IncomeSourceCardProps) => {
    const { t } = useTranslation();

    const renderIncomeSourceData = (): ReactNode => {
        const data = [];

        data.push([
            <>{t('incomeSourceCard.income.label')}&nbsp;</>,
            grossIncome ? formatPrice(grossIncome) : '-'
        ]);

        switch (incomeType) {
            case IncomeVerificationIncomeSourceTypeEnum.Employment:
                data.push([
                    <>{t('incomeSourceCard.income.contractType')}&nbsp;</>,
                    t(`incomeSourceCard.income.employment.contractType.${(incomeSource as IncomeSourceEmployment).contractType}`)
                ]);
                break;
            default:
                break;
        }

        if (data.length < 1) {
            return null;
        }

        return (
            <BulletSeparatedList
                className={styles.additionalFileDetails}
                list={data.map((item, index) => (
                    <span key={index}>{item.map((part, partIndex) => (
                        <Fragment key={partIndex}>{part}</Fragment>
                    ))}</span>
                ))}
            />
        )
    }

    return (
        <Card
            className={classNames(styles.incomeSourceCard, isDeleting && styles.isDeleting, className && className)}
            hasPadding={false}
            borderColor={ColorEnum.Gray300}
        >
            <div className={styles.gridMd}>
                <div className={styles.gridSm}>
                    <div className={styles.textContainer}>
                        <Text
                            color={ColorEnum.Gray800}
                            weight={FontWeightEnum.Semibold}
                        >{t(`incomeSourceCard.incomeType.${incomeType}`)}</Text>
                    </div>
                    <div className={styles.buttonContainer}>
                        <ButtonNew
                            variant='secondary-gray'
                            iconBefore={<PencilLineIcon />}
                            onClick={onEditClick}
                            disabled={disabled}
                        />
                        <ButtonNew
                            variant='secondary-destructive'
                            iconBefore={<DeleteBinIcon />}
                            onClick={onDeleteClick}
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div>
                    <Text
                        color={ColorEnum.Gray500}
                        weight={FontWeightEnum.Regular}
                    >
                        {renderIncomeSourceData()}
                    </Text>
                </div>
                {incomeSource && !incomeSource.isComplete && (
                    <HighlightBlock
                        type='warning'
                    >
                        {t(`screening.overview.alert.income_verification_source_is_incomplete`)}
                    </HighlightBlock>
                )}
            </div>
        </Card>
    );
};

export default IncomeSourceCard;
